import { useCallback, useEffect, useReducer, useRef } from 'react';
import type { ReferenceLibraryMeta, ReferenceSharedMetadata } from '@scoutaround/script-breakdown-shared';

import { api, ApiError, isCancelledUpload } from '../../lib/api.js';
import { formatBytes, isVideoFile, refuseFile } from './format.js';
import { grabPosterFrame } from './poster.js';
import { newRequestKey } from './hooks.js';

/** Three at a time: enough to fill a connection, few enough that one slow file does not starve the rest. */
export const UPLOAD_CONCURRENCY = 3;

export type UploadState = 'queued' | 'uploading' | 'done' | 'failed';

export interface UploadItem {
  readonly key: string;
  readonly file: File;
  /**
   * Minted once per FILE and kept: a retry after a lost answer sends the same
   * key, so the server returns the reference it already made instead of a
   * second one (design §3).
   */
  readonly clientRequestId: string;
  readonly state: UploadState;
  readonly loaded: number;
  readonly total: number;
  readonly error: string | null;
  readonly referenceId: string | null;
  /**
   * What this file is sent with, fixed at its first attempt. The server refuses
   * a different payload under the same key (409 `IDEMPOTENCY_KEY_REUSED`), so a
   * retry must not pick up a later change to the shared fields.
   */
  readonly metadata: ReferenceSharedMetadata | null;
}

export interface RejectedFile {
  readonly key: string;
  readonly name: string;
  readonly reason: string;
}

export interface UploadSummary {
  readonly total: number;
  readonly done: number;
  readonly failed: number;
  readonly active: number;
  readonly queued: number;
}

/**
 * A BULK UPLOAD THAT KEEPS WHAT WORKED.
 *
 * One `uploadReference` per file, three at a time, each with its own byte
 * progress. A failure never undoes a success: the references that were made
 * stay made, the failed ones are listed with the server's reason, and Retry
 * sends only those, under their original keys.
 *
 * Lives in the References tab rather than the drawer, so closing the drawer
 * does not orphan uploads in flight; leaving the tab cancels what is still
 * sending.
 */
export function useBulkUpload(
  projectId: string,
  upload: ReferenceLibraryMeta['upload'] | undefined,
  onUploaded: () => void,
) {
  const items = useRef<UploadItem[]>([]);
  const rejected = useRef<RejectedFile[]>([]);
  const active = useRef(0);
  const controllers = useRef(new Map<string, AbortController>());
  const posters = useRef(new Map<string, Blob | null>());
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  const uploadedRef = useRef(onUploaded);
  uploadedRef.current = onUploaded;
  const limitRef = useRef(upload);
  limitRef.current = upload;

  useEffect(() => {
    const running = controllers.current;
    return () => {
      for (const controller of running.values()) controller.abort();
    };
  }, []);

  const patch = useCallback((key: string, change: Partial<UploadItem>) => {
    items.current = items.current.map((item) => (item.key === key ? { ...item, ...change } : item));
    rerender();
  }, []);

  const failureText = useCallback((error: unknown): string => {
    if (error instanceof ApiError) {
      // nginx's 413 carries no body; say the limit the server published, not a number of ours.
      if (error.code === 'FILE_TOO_LARGE' && limitRef.current) {
        return `Larger than the ${formatBytes(limitRef.current.maxBytes)} limit.`;
      }
      return error.message;
    }
    return 'The upload did not finish. Try again.';
  }, []);

  const send = useCallback(
    async (item: UploadItem) => {
      const controller = new AbortController();
      controllers.current.set(item.key, controller);
      try {
        let poster: Blob | null = null;
        if (isVideoFile(item.file)) {
          if (!posters.current.has(item.key)) posters.current.set(item.key, await grabPosterFrame(item.file));
          poster = posters.current.get(item.key) ?? null;
          // The poster travels in the same request as the file, and nginx caps the whole
          // body at the upload limit: a file close to the limit goes without one (its
          // card shows the video icon) rather than being refused.
          const max = limitRef.current?.maxBytes;
          if (poster !== null && max !== undefined && item.file.size + poster.size > max - 256 * 1024) {
            poster = null;
          }
        }
        const created = await api.uploadReference(
          projectId,
          item.file,
          { ...(item.metadata ?? { groupIds: [], tags: [], links: [] }), clientRequestId: item.clientRequestId },
          {
            poster,
            signal: controller.signal,
            onProgress: ({ loaded, total }) => {
              patch(item.key, { loaded, total });
            },
          },
        );
        patch(item.key, {
          state: 'done',
          loaded: item.file.size,
          total: item.file.size,
          referenceId: created?.id ?? null,
          error: null,
        });
        uploadedRef.current();
      } catch (error) {
        if (isCancelledUpload(error)) return;
        patch(item.key, { state: 'failed', error: failureText(error) });
      } finally {
        controllers.current.delete(item.key);
      }
    },
    [projectId, patch, failureText],
  );

  const pump = useCallback(() => {
    while (active.current < UPLOAD_CONCURRENCY) {
      const next = items.current.find((item) => item.state === 'queued' && item.metadata !== null);
      if (next === undefined) return;
      active.current += 1;
      patch(next.key, { state: 'uploading', loaded: 0, total: next.file.size, error: null });
      void send(next).finally(() => {
        active.current -= 1;
        pump();
      });
    }
  }, [patch, send]);

  /** Checks each file against the server's limit; refused ones are listed and never sent. */
  const add = useCallback((files: readonly File[]) => {
    const limit = limitRef.current;
    for (const file of files) {
      const reason = limit ? refuseFile(file, limit) : null;
      const key = newRequestKey();
      if (reason !== null) {
        rejected.current = [...rejected.current, { key, name: file.name, reason }];
        continue;
      }
      items.current = [
        ...items.current,
        {
          key,
          file,
          clientRequestId: newRequestKey(),
          state: 'queued',
          loaded: 0,
          total: file.size,
          error: null,
          referenceId: null,
          metadata: null,
        },
      ];
    }
    rerender();
  }, []);

  /** Starts every file not yet sent, with these shared fields. */
  const start = useCallback(
    (metadata: ReferenceSharedMetadata) => {
      items.current = items.current.map((item) =>
        item.state === 'queued' && item.metadata === null ? { ...item, metadata } : item,
      );
      pump();
    },
    [pump],
  );

  /** Sends the failed files again under their original keys and fields. */
  const retryFailed = useCallback(() => {
    items.current = items.current.map((item) =>
      item.state === 'failed' ? { ...item, state: 'queued', error: null, loaded: 0 } : item,
    );
    pump();
  }, [pump]);

  /** A file not yet sent leaves the list. */
  const remove = useCallback((key: string) => {
    items.current = items.current.filter((item) => item.key !== key || item.state === 'uploading');
    rejected.current = rejected.current.filter((r) => r.key !== key);
    rerender();
  }, []);

  /** Clears the finished list once nothing is sending. */
  const reset = useCallback(() => {
    if (active.current > 0) return;
    items.current = [];
    rejected.current = [];
    posters.current.clear();
    rerender();
  }, []);

  const list = items.current;
  const summary: UploadSummary = {
    total: list.length,
    done: list.filter((i) => i.state === 'done').length,
    failed: list.filter((i) => i.state === 'failed').length,
    active: list.filter((i) => i.state === 'uploading').length,
    queued: list.filter((i) => i.state === 'queued').length,
  };
  const started = list.some((i) => i.metadata !== null);

  return { items: list, rejected: rejected.current, summary, started, add, start, retryFailed, remove, reset };
}

export type BulkUpload = ReturnType<typeof useBulkUpload>;
